import { styled, useTheme } from "@mui/material/styles";
import { Container, Grid, Box, Typography, Card } from "@mui/material";
// images and icons
import FindBestCM from "../../image/why-chargemiles/find-best-cm.jpg";
import GoMilesCM from "../../image/why-chargemiles/go-miles-cm.jpg";
import ManageYourCharging from "../../image/why-chargemiles/manage-your-charging.png";
import PlanYourCharging from "../../image/why-chargemiles/plan-your-trip.jpg";
// components
import Page from "../Page";
import { DomainText } from "../chargeMiles/DomainText";

const StyledTitleSpan = styled("span")(({ theme }) => ({
  fontFamily: '"Work Sans", sans-serif',
  textTransform: 'uppercase',
  fontSize: '2rem',
  lineHeight: '32px',
  fontWeight: 700,
  position: 'relative',
  color: theme.palette.text.primary,
  margin: '0 0 16px',
  padding: '0 50px',
  letterSpacing: '-1px',
  display: 'inline-block',
  '&:before, &:after': {
    content: '""',
    position: 'absolute',
    top: '50%',
    width: '35px',
    height: '4px',
    background: `linear-gradient(135deg, var(--app-color) 0%, ${theme.palette.primary.dark} 100%)`,
    borderRadius: '2px'
  },
  '&:before': { left: 0 },
  '&:after': { right: 0 }
}));

const StyledFeatureImage = styled("img")(({ theme }) => ({
  width: '100%',
  maxWidth: '500px',
  height: 'auto',
  borderRadius: theme.shape.borderRadius * 3,
  boxShadow: `0 12px 24px 0 rgba(145,158,171,0.24)`,
  display: 'block',
  margin: '0 auto',
  transition: 'transform 0.4s ease',
  '&:hover': {
    transform: 'scale(1.02)'
  }
}));

export default function WhyCM() {
  const theme = useTheme();

  return (
    <Page title="Why Us | ChargeMiles" sx={{ marginTop: { xs: "128px", md: "62px" }, overflow: "hidden", minHeight: '100vh', pb: 10 }}>
      <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 } }}>
        <Card sx={{ 
          boxShadow: (theme) => `0 0 4px 0 rgba(145,158,171,0.24), 0 24px 48px 0 rgba(145,158,171,0.24)`,
          borderRadius: 3,
          overflow: "hidden" 
        }}>
          {/* Header Gradient */}
          <Box
            sx={{
              background: `linear-gradient(135deg, var(--app-color) 0%, ${theme.palette.primary.dark} 100%)`,
              color: '#fff',
              p: { xs: 4, md: 6 },
              textAlign: 'center',
            }}
          >
            <Typography variant="h3" fontWeight={700} gutterBottom>
              Why <DomainText />?
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.85, maxWidth: 600, mx: 'auto' }}>
              Locate charge points, plan your electric journey, and share updates comfortably within the community.
            </Typography>
          </Box>

          <Box sx={{ p: { xs: 3, md: 6, lg: 8 } }}>
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <StyledTitleSpan theme={theme}>
                OUR FEATURES
              </StyledTitleSpan>
              <Box sx={{ mt: 4, maxWidth: 800, mx: 'auto', '& p': { mb: 1.5, color: 'text.secondary', fontSize: '1.1rem' } }}>
                <Typography variant="body1">Do you want to locate the best rapid charging stations along your route?</Typography>
                <Typography variant="body1">Are you preparing for a long trip by electric vehicle for the holidays?</Typography>
                <Typography variant="body1">Are you looking for free charging stations in the surrounding area?</Typography>
                <Typography variant="body1">Are you looking for a charging station nearby?</Typography>
              </Box>
            </Box>

            <Grid container spacing={8} alignItems="center" sx={{ mb: 10 }}>
              {/* Feature 1 */}
              <Grid item xs={12} md={5}>
                <Typography variant="h3" fontWeight={700} gutterBottom color="text.primary">
                  Find the best <br/> charging point
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph sx={{ fontSize: '1.1rem' }}>
                  Easily browse the map and locate the exact chargers you need with our real-time availability tracking.
                </Typography>
              </Grid>
              <Grid item xs={12} md={7}>
                <StyledFeatureImage src={FindBestCM} alt="find best charging point" theme={theme} />
              </Grid>

              {/* Feature 2 */}
              <Grid item xs={12} md={7} sx={{ order: { xs: 2, md: 1 } }}>
                <StyledFeatureImage src={PlanYourCharging} alt="plan your charging" theme={theme} />
              </Grid>
              <Grid item xs={12} md={5} sx={{ order: { xs: 1, md: 2 } }}>
                <Typography variant="h3" fontWeight={700} gutterBottom color="text.primary">
                  Plan Longer <br/> Electric Journeys
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph sx={{ fontSize: '1.1rem' }}>
                  Organize your pit stops along long routes effortlessly so you never run out of juice.
                </Typography>
              </Grid>

              {/* Feature 3 */}
              <Grid item xs={12} md={5} sx={{ order: 3 }}>
                <Typography variant="h3" fontWeight={700} gutterBottom color="text.primary">
                  Travel Together <br/> with EV drivers
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph sx={{ fontSize: '1.1rem' }}>
                  Embrace the community. Share reports and help fellow drivers locate functioning stations.
                </Typography>
              </Grid>
              <Grid item xs={12} md={7} sx={{ order: 4 }}>
                <StyledFeatureImage src={GoMilesCM} alt="travel together" theme={theme} />
              </Grid>

              {/* Feature 4 */}
              <Grid item xs={12} md={7} sx={{ order: { xs: 6, md: 5 } }}>
                <StyledFeatureImage src={ManageYourCharging} alt="manage your charging" theme={theme} />
              </Grid>
              <Grid item xs={12} md={5} sx={{ order: { xs: 5, md: 6 } }}>
                <Typography variant="h3" fontWeight={700} gutterBottom color="text.primary">
                  Manage your <br/> charging flow
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph sx={{ fontSize: '1.1rem' }}>
                  Track your usage metrics, stop and start transactions remotely, and securely manage your connected wallet.
                </Typography>
              </Grid>
            </Grid>

            <Box sx={{ textAlign: 'center', mb: 8, px: { xs: 0, md: 4 } }}>
              <StyledTitleSpan theme={theme}>
                Other Features
              </StyledTitleSpan>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 4, lineHeight: 1.8, fontSize: '1.1rem' }}>
                <DomainText /> allows commercial and domestic users, charging
                point providers to add a charger to the <DomainText /> Map to enable
                community charging for free or a small charge. Add new public
                charge points through the wizard, points will be reviewed by the{" "}
                <DomainText /> data team and then added to the live map.
              </Typography>
            </Box>

            <Box sx={{ textAlign: 'center', px: { xs: 0, md: 4 } }}>
              <StyledTitleSpan theme={theme}>
                A Team that Cares
              </StyledTitleSpan>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 4, lineHeight: 1.8, fontSize: '1.1rem' }}>
                <DomainText /> is also a dream team that gives their all to
                enhance the app every day with the help of your invaluable
                feedback. Any questions, suggestions, rave reviews? Please don’t
                hesitate to get in touch at <a href="mailto:support@chargemiles.ca" style={{color: theme.palette.primary.main, fontWeight: 600, textDecoration: 'none'}}>support@chargemiles.ca</a>
              </Typography>
            </Box>

          </Box>
        </Card>
      </Container>
    </Page>
  );
}
